/**
 * DotApparel Rewards Account API
 * @see https://developers.dotapparel.com.au/core/frontend/rewards-program
 */

const REWARDS_ACCOUNT_PATH = '/apps/dotapparel/api-rewardsAccount'

export interface RewardsAccountReward {
  available: string
  id: string
  created_at: string
  description: string
  expires_at: string
  redeemed: string
  total: string
}

export interface RewardsAccountGift {
  created_at: string
  description: string
  expires_at: string
  gift_id: string
  options: Array<{ handle: string; shopify_id: number }>
}

export interface RewardsAccountResponse {
  available_rewards?: string
  reward_account_id?: string
  join_date?: string
  tier_name?: string
  tier_description?: string
  tier_join_date?: string
  tier_locked_until_date?: string
  next_tier_id?: string
  next_tier_name?: string
  spend_to_next_tier?: string
  rewards?: RewardsAccountReward[]
  gifts?: RewardsAccountGift[]
}

export interface RewardsAccountApiResult {
  ok: boolean
  data: RewardsAccountResponse | { data?: RewardsAccountResponse; errors?: Array<{ detail?: string }> }
}

/** API can return { data: { available_rewards, ... } } or { data: { data: { available_rewards, ... } } }. Normalize to inner account data. */
export function getAccountFromResult(result: RewardsAccountApiResult): RewardsAccountResponse | null {
  const raw = result?.data
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const withNested = raw as { data?: RewardsAccountResponse }
  if (withNested.data && typeof withNested.data === 'object' && !Array.isArray(withNested.data)) {
    return withNested.data
  }
  return raw as RewardsAccountResponse
}

function normalizeRewardsData(raw: RewardsAccountApiResult['data']): RewardsAccountResponse | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const withNested = raw as { data?: RewardsAccountResponse }
  if (withNested.data && typeof withNested.data === 'object' && !Array.isArray(withNested.data)) {
    return withNested.data
  }
  return raw as RewardsAccountResponse
}

let rewardsCache: { email: string; data: RewardsAccountResponse } | null = null
const pendingByEmail = new Map<string, Promise<RewardsAccountApiResult>>()

/**
 * Returns cached rewards account data for the given email, if any.
 * Use this to avoid a flash of default content when the badge mounts after an early fetch.
 */
export function getCachedRewardsAccount(email: string): RewardsAccountResponse | null {
  if (!rewardsCache || rewardsCache.email !== email) return null
  return rewardsCache.data
}

/**
 * Fetches the rewards account for the given email from the DotApparel API.
 * Result is cached by email. If a fetch for this email is already in flight, returns that same promise.
 */
export async function fetchRewardsAccount(email: string): Promise<RewardsAccountApiResult> {
  const pending = pendingByEmail.get(email)
  if (pending) return pending

  const promise = doFetchRewardsAccount(email)
  pendingByEmail.set(email, promise)
  promise.finally(() => pendingByEmail.delete(email))
  return promise
}

async function doFetchRewardsAccount(email: string): Promise<RewardsAccountApiResult> {
  const url = `${window.location.origin}${REWARDS_ACCOUNT_PATH}`
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  })
  const body = await res.json().catch(() => ({}))
  const result: RewardsAccountApiResult = { ok: res.ok, data: body }
  const account = normalizeRewardsData(result.data)
  if (result.ok && account) {
    rewardsCache = { email, data: account }
  }
  return result
}

/**
 * If the current user is a logged-in customer, fetches their rewards account and logs the response (for testing).
 * Call once after theme load (e.g. from theme.ts).
 */
export function fetchRewardsAccountWhenCustomer(): void {
  const globals = typeof window !== 'undefined' ? window.globals : undefined
  if (!globals?.customer?.email) return

  const email = globals.customer.email
  fetchRewardsAccount(email)
    .then((result) => {
      // console.log('[DotApparel Rewards] Request email:', email)
      // console.log('[DotApparel Rewards] Response:', result)
      if (!result.ok) {
        console.warn('[DotApparel Rewards] Non-OK response:', result.data)
      }
    })
    .catch((err) => {
      console.error('[DotApparel Rewards] Fetch error:', err)
    })
}
